import BookIcon from "@ecologi/assets/icons/book.svg";
import LocationIcon from "@ecologi/assets/icons/location_alt.svg";
import {
  getPrimaryProjectLocation,
  getProjectMapUrl,
  Impact,
  isSubProfileType,
  isTree,
  pluralise,
  PublicProjectSummary,
  SubProfile,
  Tile,
  TileVariant,
  User,
} from "@ecologi/shared";
import styled from "@emotion/styled";
import React from "react";
import { ForestModalBase } from "./ForestModalBase";
import { ModalInfoCard } from "./ModalInfoCard";
import { ReferralUserLink } from "./ReferralUserLink";
import {
  getImpactDate,
  getFundedBy,
  getLocationAndProjectLabel,
  getModalImagePath,
  getTitle,
  getTreeNumberingText,
} from "./utils";
import { COUNTRY_ICONS } from "./consts";
import {
  ProjectContent,
  Title,
  TitleWrapper,
  Text,
  Grid,
  GridIcon,
  StyledLink,
  Bold,
} from "./styles/ForestModal.styles";
import { ImpactFundDisclaimer } from "./ImpactFundDisclaimer";

const ContentWrapper = styled.div`
  margin-bottom: 10px;
`;

const LinkWrapper = styled.div`
  width: fit-content;
`;

const SubProfileWrapper = styled.div`
  background-color: ${({ theme }) => theme.colors.mist};
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 30px 40px 40px 30px;
`;

const SubProfileTitle = styled.div`
  font-weight: bold;
`;

export const ForestTreeModal = ({
  closePopup,
  isOpen,
  isOwnProfile,
  project,
  tileInfo: { tile, impact, isMocked },
  user,
}: {
  closePopup: () => void;
  isOpen: boolean;
  isOwnProfile: boolean;
  project?: PublicProjectSummary;
  tileInfo: { tile: Tile; impact: Impact; isMocked: boolean };
  user: User | null;
}) => {
  if (!isTree(impact)) return null;

  const subProfile = isSubProfileType(tile) && new SubProfile(tile);

  const title = subProfile
    ? `${isSubProfileType(tile) && tile.username} sub-profile`
    : getTitle(impact);

  const fundedBy = getFundedBy(user, impact);
  const impactDate = getImpactDate(impact);

  const [location, projectLabel] =
    (project && getLocationAndProjectLabel(impact, project)) || [];

  const projectHref = project ? `/projects/${project.slug}` : undefined;
  const projectMapUrl = getProjectMapUrl(
    project ? getPrimaryProjectLocation(project) : undefined
  );

  const isReferral = impact.variant === TileVariant.REFERRAL;
  const referralUserId = impact.referralUser?.userId || impact.name;

  const hasFundedInfo = impactDate && !isReferral;

  const numberingText = getTreeNumberingText(impact);
  const numberingLabel = `${pluralise(impact.value, "Tree number", false)}: `;
  return (
    <ForestModalBase
      isPending={impact.isPending}
      closePopup={closePopup}
      countryFlag={COUNTRY_ICONS[impact.localProject?.country]?.flag}
      enableSharing={!isMocked && !subProfile}
      imagePath={getModalImagePath(impact, project, !!subProfile)}
      isOpen={isOpen}
      isSubProfile={!!subProfile}
    >
      <>
        <ProjectContent>
          <TitleWrapper>
            <Title data-testid="forest-modal-project-title">{title}</Title>
          </TitleWrapper>

          {!!subProfile ? (
            <SubProfileWrapper data-testid="subprofile-modal-data">
              <SubProfileTitle>What's this?</SubProfileTitle>
              <ContentWrapper>
                {subProfile.name} is like a little village in the bigger town of{" "}
                {user?.username}. They're part of the group, but are also doing
                their own thing to protect the planet.
              </ContentWrapper>
              <LinkWrapper>
                <StyledLink
                  href={`/${user?.username}/${subProfile.name}`}
                  target="_blank"
                  title="View sub-profile"
                  rel="noopener"
                >
                  Check out their impact here.
                </StyledLink>
              </LinkWrapper>
            </SubProfileWrapper>
          ) : (
            <>
              {hasFundedInfo && (
                <>
                  {fundedBy && (
                    <Text data-testid="forest-modal-funded-by">
                      <Bold>Funded by:</Bold> {fundedBy}
                    </Text>
                  )}
                  <Text data-testid="forest-modal-date">
                    <Bold>
                      {`Date ${impact.isPending ? "ordered" : "funded"}: `}
                    </Bold>
                    {impactDate}
                  </Text>
                </>
              )}

              {numberingText && (
                <Text data-testid="forest-modal-tree-numbers">
                  <Bold>{numberingLabel}</Bold>
                  {numberingText}
                </Text>
              )}

              {isReferral && referralUserId && (
                <Text>
                  <Bold>{"Referral: "}</Bold>
                  <ReferralUserLink userId={referralUserId} />
                </Text>
              )}

              <Grid>
                {location && (
                  <>
                    <GridIcon>
                      <LocationIcon />
                    </GridIcon>

                    <Text>
                      <Bold>Location: </Bold>
                      {impact.what3Words || projectMapUrl ? (
                        <StyledLink
                          href={
                            projectMapUrl ||
                            `https://what3words.com/${impact.what3Words}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          title={
                            projectMapUrl
                              ? `View the project in ${location} (new tab)`
                              : "View your tree on the What3Words map (new tab)"
                          }
                        >
                          {location}
                        </StyledLink>
                      ) : (
                        location
                      )}
                    </Text>
                  </>
                )}

                {projectLabel && projectHref && (
                  <>
                    <GridIcon>
                      <BookIcon />
                    </GridIcon>

                    <Text>
                      <Bold>Project: </Bold>
                      <StyledLink
                        href={projectHref}
                        rel="noopener noreferrer"
                        target="_blank"
                        title="View project"
                      >
                        {projectLabel}
                      </StyledLink>
                    </Text>
                  </>
                )}
              </Grid>
            </>
          )}
        </ProjectContent>

        <ModalInfoCard impact={impact} isOwnProfile={isOwnProfile} />
        {impact.variant === TileVariant.IMPACT_FUND ? (
          <ImpactFundDisclaimer />
        ) : null}
      </>
    </ForestModalBase>
  );
};
